import { useDroppable } from '@dnd-kit/core'
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import React, { useMemo } from 'react'

import { useAppSelector } from 'store/hooks'
import { getWidgetDimensions } from '../../../constants/constants'
import { WidgetConfig, WidgetRow } from '../../../types'
import { RowGrid, RowInsertZone, RowWrapper } from '../styles/rowStyles'
import { SortableWidget } from './SortableWidget'

interface RowContainerProps {
	row: WidgetRow
	activeId: string | null
	onReorderWidgets: (widgets: WidgetConfig[]) => void
	onDropWidget: (widget: WidgetConfig) => void
	customCellSize?: number
	showInsertZoneBefore?: boolean
	showInsertZoneAfter?: boolean
}

export const RowContainer: React.FC<RowContainerProps> = ({
	row,
	activeId,
	customCellSize,
	showInsertZoneBefore = false,
	showInsertZoneAfter = false,
}) => {
	const { isEditMode } = useAppSelector(state => state.dashboardSlice)

	const { setNodeRef, isOver } = useDroppable({
		id: `row-${row.id}`,
		data: {
			type: 'row',
			rowId: row.id,
		},
	})

	const { setNodeRef: setBeforeZoneRef, isOver: isBeforeZoneOver } =
		useDroppable({
			id: `row-insert-before-${row.id}`,
			data: {
				type: 'row-insert',
				insertAfterRowId: null,
				rowId: row.id,
			},
		})

	const { setNodeRef: setAfterZoneRef, isOver: isAfterZoneOver } = useDroppable(
		{
			id: `row-insert-after-${row.id}`,
			data: {
				type: 'row-insert',
				insertAfterRowId: row.id,
				rowId: row.id,
			},
		},
	)

	// Показываем зоны визуально только в режиме редактирования и когда на них наводят виджет
	const showBeforeZone = isEditMode && isBeforeZoneOver && showInsertZoneBefore
	const showAfterZone = isEditMode && isAfterZoneOver && showInsertZoneAfter
	const isDraggingActive = isEditMode && !!activeId

	// Проверяем, есть ли в строке FULL виджет
	const hasFullWidget = useMemo(() => {
		return row.widgets.some(widget => {
			const dimensions = getWidgetDimensions(widget.size)
			return dimensions.w === -1
		})
	}, [row.widgets])

	return (
		<div style={{ width: '100%' }}>
			{/* Всегда рендерим зоны для возможности hover на всю ширину */}
			{showInsertZoneBefore && (
				<RowInsertZone
					ref={setBeforeZoneRef}
					isOver={isBeforeZoneOver}
					isVisible={showBeforeZone}
					isDraggingActive={isDraggingActive}
				/>
			)}
			<RowWrapper ref={setNodeRef}>
				<SortableContext
					items={row.widgets.map(w => w.id)}
					strategy={rectSortingStrategy}
				>
					<RowGrid
						customCellSize={customCellSize}
						hasFullWidget={hasFullWidget}
						style={{
							backgroundColor: isOver ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
							borderRadius: isOver ? '8px' : '0',
							transition: 'background-color 0.2s ease',
						}}
					>
						{row.widgets.map(widget => (
							<SortableWidget
								key={widget.id}
								widget={widget}
								isDragging={widget.id === activeId}
								customCellSize={customCellSize}
								rowId={row.id}
							/>
						))}
					</RowGrid>
				</SortableContext>
			</RowWrapper>
			{/* Всегда рендерим зоны для возможности hover на всю ширину */}
			{showInsertZoneAfter && (
				<RowInsertZone
					ref={setAfterZoneRef}
					isOver={isAfterZoneOver}
					isVisible={showAfterZone}
					isDraggingActive={isDraggingActive}
				/>
			)}
		</div>
	)
}
